/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Alert, Button, ButtonGroup, Heading, HeadingSection, HFlow, Icon, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { useAlert } from 'components/alert'
import { useErrorHandler } from 'components/error'
import { CheckboxField, Form, FormRenderProps } from 'components/form'
import { PageContent } from 'components/layout/PageContent'
import { confirm } from 'components/modals/confirm'
import { useCancelarAtendimentoMutation, useFinalizarAtendimentoVacinacaoMutation } from 'graphql/hooks.generated'
import React, { Dispatch, useCallback, useMemo, useState } from 'react'
import { FormSpy } from 'react-final-form'
import { useHistory } from 'react-router'
import { sexoByIdentidadeGenero } from 'util/sexoByIdentidadeGenero'
import { convertAtendimentoVacinacaoModelToInput } from 'view/atendimentos/atendimento-vacinacao/graphql/converter'
import { AtendimentoVacinacaoState, meta } from 'view/atendimentos/atendimento-vacinacao/model'
import {
  getDescricaoTituloCalendario,
  getGrupoAlvoVacinacao,
} from 'view/atendimentos/atendimento-vacinacao/vacinacaoUtils'
import { AgendamentoDia } from 'view/atendimentos/types/AgendamentoDia'
import { CidadaoAtendimento } from 'view/atendimentos/types/CidadaoAtendimento'

import { AsideView } from '../soap/aside/AsideView'
import { DesfechoAtendimentoPanel } from '../soap/desfecho/components/DesfechoAtendimentoPanel'
import { createVacinacaoCalculator } from './calculator'
import { VacinacaoCalendarioView } from './VacinacaoCalendarioView'

interface VacinacaoViewProps {
  atendimentoId: ID
  prontuarioId: ID
  cidadao: CidadaoAtendimento
  dataAtendimento: Instant
  clearCache: Dispatch<void>
  agendamentosDia?: AgendamentoDia[]
  initialValues: AtendimentoVacinacaoState
}

export type EstadoBotaoCalendario = 'CALENDARIO_VACINAL' | 'OUTROS_IMUNOS'

export function VacinacaoView(props: VacinacaoViewProps) {
  const { prontuarioId, initialValues, cidadao, dataAtendimento, agendamentosDia, atendimentoId, clearCache } = props

  const [estadoBotaoCalendario, setEstadoBotaoCalendario] = useState<EstadoBotaoCalendario>('CALENDARIO_VACINAL')
  const [cancelar] = useCancelarAtendimentoMutation()
  const [finalizar] = useFinalizarAtendimentoVacinacaoMutation()
  const handleRejection = useErrorHandler()
  const grupoAlvoCidadao = getGrupoAlvoVacinacao(cidadao.dataNascimento, dataAtendimento)
  const isSexoFeminino = sexoByIdentidadeGenero(cidadao.sexo, cidadao.identidadeGeneroDbEnum) === 'FEMININO'

  const theme = useTheme()
  const style = useMemo(() => styles(theme), [theme])

  const history = useHistory()
  const alert = useAlert()

  const handleCancelar = useCallback(
    () =>
      confirm({
        title: 'Deseja cancelar o atendimento de vacinação?',
        body: 'As alterações realizadas serão perdidas.',
        confirmLabel: 'Sim',
        cancelLabel: 'Não',
        onConfirm: () => {
          cancelar({ variables: { atendimentoId } })
            .then(() => {
              clearCache()
              history.push('/lista-atendimento')
            })
            .catch(handleRejection)
        },
      })(),
    [atendimentoId, cancelar, clearCache, handleRejection, history]
  )

  const handleFinalizarVacinacao = useCallback(
    (values: AtendimentoVacinacaoState) =>
      finalizar({
        variables: {
          input: convertAtendimentoVacinacaoModelToInput(atendimentoId, cidadao.id, values),
        },
      }).then(() => {
        alert('success', `Atendimento de vacinação finalizado com sucesso.`)
        history.push('/lista-atendimento')
      }),
    [alert, atendimentoId, cidadao.id, finalizar, history]
  )

  const renderVacinacao = useCallback(
    (formProps: FormRenderProps<AtendimentoVacinacaoState>) => {
      const { handleSubmit } = formProps
      return (
        <React.Fragment>
          <div
            css={css`
              grid-area: 'main';
              padding: 0 1rem 1rem;
            `}
          >
            <HeadingSection level={2} title='Calendário vacinal'>
              <VFlow>
                <VFlow vSpacing={0.3}>
                  <Text fontWeight='bold'>Condições</Text>
                  <div css={isSexoFeminino ? style.box : style.boxNoBorder}>
                    <VFlow>
                      <HFlow>
                        {isSexoFeminino && (
                          <>
                            <CheckboxField name={meta.condicoesVacinacao.gestante} label='Gestante' />
                            <CheckboxField
                              name={meta.condicoesVacinacao.puerpera}
                              label='Puérpera'
                              style={style.separator}
                            />
                          </>
                        )}
                        <CheckboxField name={meta.condicoesVacinacao.viajante} label='Viajante' />
                      </HFlow>
                    </VFlow>
                  </div>
                </VFlow>
                <ButtonGroup>
                  <Button
                    kind={estadoBotaoCalendario === 'CALENDARIO_VACINAL' ? 'primary' : 'normal'}
                    size='small'
                    skin='default'
                    onClick={() => setEstadoBotaoCalendario('CALENDARIO_VACINAL')}
                  >
                    Calendário vacinal{' '}
                    {getDescricaoTituloCalendario(grupoAlvoCidadao) +
                      (formProps.values.condicoesVacinacao.gestante ? ' / gestante' : '')}
                  </Button>
                  <Button
                    kind={estadoBotaoCalendario === 'OUTROS_IMUNOS' ? 'primary' : 'normal'}
                    size='small'
                    skin='default'
                    onClick={() => setEstadoBotaoCalendario('OUTROS_IMUNOS')}
                  >
                    Outros imunobiológicos
                  </Button>
                </ButtonGroup>
                <VacinacaoCalendarioView
                  cidadao={cidadao}
                  gestante={formProps.values.condicoesVacinacao.gestante}
                  dataAtendimento={dataAtendimento}
                  estadoBotaoCalendario={estadoBotaoCalendario}
                  grupoAlvoCidadao={grupoAlvoCidadao}
                  prontuarioId={prontuarioId}
                  registros={formProps.values.registroVacinacao}
                />
                <Heading color='normal' level={2} style={style.headingDesfecho}>
                  Dados do desfecho
                </Heading>
                <VFlow>
                  <Button kind='primary' skin='outline' size='small' onClick={(event) => {}}>
                    <Icon icon='calendarOutline' /> Agendar consulta
                  </Button>
                  <DesfechoAtendimentoPanel
                    name={meta.desfechoVacinacao.desfechoAtendimento}
                    agendamentosDia={agendamentosDia}
                  />
                  <Button kind='primary' size='small' onClick={() => {}}>
                    <Icon
                      icon='plus'
                      style={css`
                        margin-right: 0.5rem;
                      `}
                    />{' '}
                    Gerar declaração de comparecimento
                  </Button>
                </VFlow>
              </VFlow>
            </HeadingSection>
          </div>
          <div css={style.actions}>
            <div css={style.sideBar}></div>
            <HFlow justifyContent='space-between'>
              <FormSpy<AtendimentoVacinacaoState> subscription={{ dirty: true, active: true, values: true }}>
                {(props) => (
                  <Alert inline type='success' styles={{ wrapper: style.draftAlertWrapper }}>
                    Rascunho salvo automaticamente às
                  </Alert>
                )}
              </FormSpy>
              <HFlow>
                <CheckboxField
                  name={meta.imprimirAtendimento}
                  label='Imprimir atendimento ao finalizar'
                  style={style.print}
                />
                <Button kind='normal' onClick={handleCancelar}>
                  Cancelar atendimento de vacinação
                </Button>
                <Button kind='primary' onClick={handleSubmit}>
                  Finalizar atendimento de vacinação
                </Button>
              </HFlow>
            </HFlow>
          </div>
        </React.Fragment>
      )
    },
    [
      isSexoFeminino,
      style.box,
      style.boxNoBorder,
      style.separator,
      style.headingDesfecho,
      style.actions,
      style.sideBar,
      style.print,
      style.draftAlertWrapper,
      estadoBotaoCalendario,
      grupoAlvoCidadao,
      cidadao,
      dataAtendimento,
      prontuarioId,
      agendamentosDia,
      handleCancelar,
    ]
  )

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        min-height: 60vh;
      `}
    >
      <PageContent fluid type='filled'>
        <div css={style.container}>
          <div css={style.aside}>
            <AsideView prontuarioId={prontuarioId} dataAtendimento={dataAtendimento} cidadaoId={cidadao.id} />
          </div>
          <Form<AtendimentoVacinacaoState>
            render={renderVacinacao}
            onSubmit={handleFinalizarVacinacao}
            decorators={[createVacinacaoCalculator]}
            initialValues={initialValues}
          />
        </div>
      </PageContent>
    </div>
  )
}

const styles = (theme: Theme) => {
  return {
    container: css`
      display: grid;
      grid-template-columns: 0.4fr 1fr;
      grid-template-rows: auto;
      grid-template-areas: 'sidebar main' 'footer footer';
    `,

    aside: css`
      width: 100%;
      max-width: 27rem;
    `,

    print: css`
      border: 0.063rem solid ${theme.pallete.gray.c80};
      border-radius: 0.125rem;
      padding: 0.75rem 1rem;
    `,

    actions: css`
      height: 5.125rem;
      position: relative;
      grid-area: footer;
      padding-top: 1rem;
      margin-bottom: 1rem;
    `,

    draftAlertWrapper: css`
      background-color: transparent;
      border-width: 0;
    `,

    headingDesfecho: css`
      padding: 0.5rem 0 0.5rem 0;
      background-color: ${theme.pallete.gray.c90};
    `,

    separator: css`
      border-right: 1px solid ${theme.pallete.divider};
      padding-right: 1rem;
    `,

    box: css`
      padding: 1rem;
      width: 22rem;
      border: 1px solid ${theme.pallete.gray.c80};
    `,

    boxNoBorder: css`
      padding: 1rem 0rem;
    `,

    sideBar: css`
      width: 100vw;
      position: relative;
      left: 50%;
      right: 50%;
      margin-left: -53vw;
      margin-right: -50vw;
      margin-bottom: 1rem;
      border-bottom: 1px solid ${theme.pallete.divider};
    `,
  }
}
